import type { ReactNode } from "react";
import type { WorkflowNodeExecutionState } from "@/stores/workflowRunsStore";

/** Max nesting depth the recursive renderer system descends before falling back
 *  to the raw JSON tree — guards against deeply-nested or cyclic result objects. */
export const MAX_RENDER_DEPTH = 6;

/** Priority bands — higher wins. See spec §"Registry & Renderer Contract". */
export const PRIORITY = {
  STRUCTURAL: 100,
  STRONG_SCALAR: 80,
  CONTEXT_HINTED: 60,
  CONTENT_SNIFFED: 40,
  PRIMITIVE: 20,
  FALLBACK: 0,
} as const;

export interface RenderContext {
  /** Node type, e.g. "ai-llm-chat", "send-erc20-token". Undefined at nested depths if unknown. */
  nodeType?: string;
  nodeId: string;
  /** Object key this value sits under, if any. */
  key?: string;
  /** Full path from the result root. */
  path: (string | number)[];
  depth: number;
  /** Sibling object this value belongs to — lets renderers read e.g. a sibling `decimals`. */
  parent?: Record<string, unknown>;
  /** Full run execution state — reused by FallbackRenderer for variable resolution. */
  executionState: Record<string, WorkflowNodeExecutionState>;
}

export interface MatchResult {
  priority: number;
  /** Used by the renderer registry as a tie-breaker when two renderers return the same priority. */
  confidence?: "high" | "low";
}

export interface ValueRendererProps {
  value: unknown;
  ctx: RenderContext;
}

export interface ValueRenderer {
  id: string;
  /** Return null / a 0-priority result for "no match". Must be pure and total. */
  match(value: unknown, ctx: RenderContext): MatchResult | null;
  Component: (props: ValueRendererProps) => ReactNode;
}
